import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    Moon,
    Sun,
    BookOpen,
    Clock,
    Sparkles,
    Heart,
    Droplets,
    CheckCircle2,
    Circle,
    Calendar,
    Award,
    Share2,
    Compass,
    Apple,
    Flame,
    RefreshCw,
    ChevronRight,
    Info,
    Bookmark,
    Check,
    MapPin,
    ArrowRight,
    Copy,
    HeartHandshake,
    Star,
    Search,
    X
} from 'lucide-react';
import Swal from 'sweetalert2';
import AnimatedSection from '../../components/common/AnimatedSection';
import { getHijriDate } from '../../utils/hijri';

// Daftar kota & offset waktu imsakiyah representatif di Indonesia
interface CityPrayerTimes {
    id: string;
    name: string;
    province: string;
    imsak: string;
    subuh: string;
    terbit: string;
    dzuhur: string;
    ashar: string;
    maghrib: string;
    isya: string;
}

const CITY_SCHEDULES: Record<string, CityPrayerTimes> = {
    jakarta: {
        id: 'jakarta',
        name: 'DKI Jakarta & Sekitarnya',
        province: 'Jabodetabek',
        imsak: '04:28',
        subuh: '04:38',
        terbit: '05:51',
        dzuhur: '12:01',
        ashar: '15:12',
        maghrib: '18:04',
        isya: '19:13'
    },
    bandung: {
        id: 'bandung',
        name: 'Kota Bandung & Sekitarnya',
        province: 'Jawa Barat',
        imsak: '04:25',
        subuh: '04:35',
        terbit: '05:48',
        dzuhur: '11:58',
        ashar: '15:10',
        maghrib: '18:02',
        isya: '19:11'
    },
    surabaya: {
        id: 'surabaya',
        name: 'Surabaya & Sekitarnya',
        province: 'Jawa Timur',
        imsak: '04:04',
        subuh: '04:14',
        terbit: '05:27',
        dzuhur: '11:37',
        ashar: '14:49',
        maghrib: '17:40',
        isya: '18:49'
    },
    yogyakarta: {
        id: 'yogyakarta',
        name: 'DI Yogyakarta & Sekitarnya',
        province: 'DIY',
        imsak: '04:15',
        subuh: '04:25',
        terbit: '05:38',
        dzuhur: '11:48',
        ashar: '15:00',
        maghrib: '17:51',
        isya: '19:00'
    },
    medan: {
        id: 'medan',
        name: 'Kota Medan & Sekitarnya',
        province: 'Sumatera Utara',
        imsak: '04:58',
        subuh: '05:08',
        terbit: '06:21',
        dzuhur: '12:31',
        ashar: '15:43',
        maghrib: '18:34',
        isya: '19:43'
    },
    makassar: {
        id: 'makassar',
        name: 'Kota Makassar & Sekitarnya',
        province: 'Sulawesi Selatan',
        imsak: '04:38',
        subuh: '04:48',
        terbit: '06:01',
        dzuhur: '12:11',
        ashar: '15:23',
        maghrib: '18:14',
        isya: '19:23'
    }
};

// Data Menu Sehat Muslimah Ramadhan
interface HealthyMenu {
    id: string;
    title: string;
    category: 'sahur' | 'iftar' | 'takjil';
    calories: number;
    protein: number;
    carbs: number;
    fiber: number;
    tag: string;
    description: string;
    ingredients: string[];
    tips: string;
}

const HEALTHY_MENUS: HealthyMenu[] = [
    // --- SAHUR PADAT NUTRISI (7 MENU) ---
    {
        id: 'm1',
        title: 'Oatmeal Kurma Chia Seed + Telur Rebus',
        category: 'sahur',
        calories: 420,
        protein: 22,
        carbs: 54,
        fiber: 8,
        tag: 'Sustained Energy',
        description: 'Kombinasi karbohidrat kompleks dan protein tinggi yang dicerna perlahan, menjaga rasa kenyang dan stamina stabil hingga sore hari.',
        ingredients: ['5 sdm Rolled Oats utuh', '3 butir Kurma Sukari dipotong dadu', '1 sdm Biji Chia (Chia Seed)', '200 ml Susu Almond / Susu Rendah Lemak', '2 butir Telur Rebus matang'],
        tips: 'Konsumsi saat sahur menjelang imsak untuk cadangan glikogen dan asam amino yang optimal sepanjang hari.'
    },
    {
        id: 'm2',
        title: 'Nasi Merah, Dada Ayam Panggang & Sayur Bening Bayam',
        category: 'sahur',
        calories: 480,
        protein: 35,
        carbs: 50,
        fiber: 6,
        tag: 'Tinggi Protein & Zat Besi',
        description: 'Menu sahur seimbang tanpa minyak berlebih, menjaga kadar hemoglobin muslimah tetap optimal selama berpuasa.',
        ingredients: ['1 centong Nasi Merah hangat', '100g Dada Ayam fillet marinasi ketumbar bawang putih', '1 mangkuk Sayur Bening Bayam & Jagung Manis', '1 potong Tahu/Tempe kukus'],
        tips: 'Kuah sayur bening bayam kaya magnesium dan potasium untuk mencegah kram otot dan dehidrasi.'
    },
    {
        id: 'm3',
        title: 'Overnight Oats Alpukat & Susu Kedelai',
        category: 'sahur',
        calories: 410,
        protein: 16,
        carbs: 48,
        fiber: 10,
        tag: 'Praktis 5 Menit Tanpa Masak',
        description: 'Solusi sahur super kilat bagi muslimah sibuk; cukup siapkan malam hari sebelum tidur, langsung santap lezat saat sahur.',
        ingredients: ['5 sdm Rolled Oats', '1/2 buah Alpukat mentega dihaluskan', '200 ml Susu Kedelai tawar hangat/dingin', '1 sdm Madu murni', 'Taburan kacang almond panggang'],
        tips: 'Lemak tak jenuh tunggal alpukat memperlambat pengosongan lambung sehingga kenyang bertahan hingga 10-12 jam.'
    },
    {
        id: 'm4',
        title: 'Sup Ayam Kampung Bening Jahe & Nasi Beras Coklat',
        category: 'sahur',
        calories: 380,
        protein: 30,
        carbs: 38,
        fiber: 7,
        tag: 'Hangat & Hidrasi Optimal',
        description: 'Kuah kaldu rempah ayam kampung dengan irisan jahe segar, menghangatkan lambung di waktu fajar dan menjaga imunitas tubuh.',
        ingredients: ['1 potong Ayam Kampung rebus empuk', '1 mangkuk kuah kaldu rempah bawang putih & jahe geprek', 'Wortel, buncis, seledri segar', '1 centong Nasi Beras Coklat / Shirataki'],
        tips: 'Kaldu tulang ayam kaya asam amino glisin dan kolagen alami yang menenangkan mukosa lambung saat puasa.'
    },
    {
        id: 'm5',
        title: 'Omelet Bayam Jamur Keju & Roti Gandum Panggang',
        category: 'sahur',
        calories: 430,
        protein: 24,
        carbs: 36,
        fiber: 6,
        tag: 'Tinggi Kalsium & Kolin',
        description: 'Sarapan fajar modern kaya protein telur utuh, jamur kancing berserat, dan karbohidrat lambat cerna dari gandum utuh.',
        ingredients: ['2 butir Telur Ayam (kocok lepas)', '1 genggam Bayam iris halus', '50g Jamur kancing tumis sedikit minyak zaitun', '1 lembar Keju slice rendah lemak', '2 lembar Roti Gandum Utuh panggang'],
        tips: 'Gunakan wajan anti-lengket dengan sedikit olesan minyak zaitun agar kadar lemak jenuh tetap terkontrol.'
    },
    {
        id: 'm6',
        title: 'Bubur Quinoa Labu Kuning & Daging Cincang Lembut',
        category: 'sahur',
        calories: 440,
        protein: 28,
        carbs: 52,
        fiber: 8,
        tag: 'Ramah Lambung & Anti-GERD',
        description: 'Tekstur lembut mudah dicerna namun kaya serat larut dan bebas gluten, sangat ideal bagi muslimah dengan riwayat lambung sensitif.',
        ingredients: ['1/2 cup Quinoa masak lunak', '100g Labu Kuning kukus haluskan', '75g Daging Sapi cincang tanpa lemak tumis kecap asin organik', 'Taburan daun bawang dan biji wijen'],
        tips: 'Labu kuning mengandung pektin yang melapisi dinding lambung dari asam berlebih saat berpuasa.'
    },
    {
        id: 'm7',
        title: 'Urap Sayuran Segar, Pepes Ikan Kembung & Nasi Merah',
        category: 'sahur',
        calories: 460,
        protein: 32,
        carbs: 46,
        fiber: 9,
        tag: 'Kaya Omega-3 Nusantara',
        description: 'Hidangan tradisional kaya antioksidan kencur dan kunyit; ikan kembung lokal terbukti memiliki Omega-3 setara atau lebih tinggi dari salmon.',
        ingredients: ['1 ekor Ikan Kembung bumbu pepes daun kemangi', '1 porsi Urap Sayur (kacang panjang, tauge, kangkung, kelapa sangrai minim)', '1 centong Nasi Merah hangat'],
        tips: 'Mengukus ikan dalam bungkusan daun pisang mengunci kelembapan dan nutrisi tanpa minyak goreng berlebih.'
    },

    // --- TAKJIL RENDAH GULA (7 MENU) ---
    {
        id: 'm8',
        title: 'Kurma Sukari + Air Kelapa Murni & Semangka',
        category: 'takjil',
        calories: 190,
        protein: 2,
        carbs: 45,
        fiber: 4,
        tag: 'Anti-Spike Gula Darah',
        description: 'Menu pembuka puasa sesuai sunnah Rasulullah SAW, memulihkan energi seketika tanpa lonjakan drastis kadar gula darah.',
        ingredients: ['3 butir Kurma Sukari / Ajwa', '250 ml Air Kelapa Murni tanpa gula', '3 potong Buah Semangka dingin'],
        tips: 'Kaya elektrolit alami (kalium & natrium) yang langsung mengembalikan hidrasi seluler tubuh.'
    },
    {
        id: 'm9',
        title: 'Smoothie Pisang Bayam Kurma dengan Susu Almond',
        category: 'takjil',
        calories: 210,
        protein: 5,
        carbs: 42,
        fiber: 4,
        tag: 'Detoks & Prebiotik',
        description: 'Minuman takjil padat nutrisi yang menyegarkan saluran cerna dan membantu pergerakan usus tetap lancar saat berpuasa.',
        ingredients: ['1 buah Pisang Cavendish matang', '1 genggam Baby Spinach (Bayam segar)', '2 butir Kurma tanpa biji', '200 ml Susu Almond tanpa pemanis', 'Es batu secukupnya'],
        tips: 'Blender halus tanpa gula tambahan, rasa manis legit alami sudah didapat dari buah kurma dan pisang.'
    },
    {
        id: 'm10',
        title: 'Puding Chia Seed Coklat Kurma & Buah Berry',
        category: 'takjil',
        calories: 220,
        protein: 6,
        carbs: 32,
        fiber: 11,
        tag: 'Superfood Bebas Gula',
        description: 'Sensasi puding lembut berbahan biji chia dan coklat murni (raw cacao) yang meredakan keinginan makan manis berlebih (sugar craving).',
        ingredients: ['3 sdm Biji Chia', '150 ml Susu Rendah Lemak / Susu Kedelai', '1 sdm Raw Cacao Powder', '1 sdm Madu Murni / Gula Aren', 'Topping Strawberry atau Buah Naga'],
        tips: 'Serat gel chia seed mengikat cairan dan memperlambat penyerapan glukosa ke aliran darah.'
    },
    {
        id: 'm11',
        title: 'Es Blewah Timun Suri Selasih Madu Jeruk Nipis',
        category: 'takjil',
        calories: 130,
        protein: 2,
        carbs: 30,
        fiber: 5,
        tag: 'Segar Tropis Rendah Kalori',
        description: 'Versi sehat dari takjil khas nusantara; sirup botol diganti dengan madu murni dan perasan jeruk nipis segar kaya vitamin C.',
        ingredients: ['1 mangkuk serutan Daging Blewah matang', '1/2 mangkuk serutan Timun Suri lembut', '1 sdt Biji Selasih mekar', '2 sdm Madu Murni', '1 sdm Air Jeruk Nipis segar', 'Air es mineral'],
        tips: 'Blewah dan timun suri mengandung 95% air yang memberi efek pendingin alami bagi suhu tubuh pasca puasa.'
    },
    {
        id: 'm12',
        title: 'Kolak Pisang Ubi Ungu Tanpa Santan (Kuah Susu Oat)',
        category: 'takjil',
        calories: 240,
        protein: 5,
        carbs: 48,
        fiber: 6,
        tag: 'Kaya Antosianin & Low Fat',
        description: 'Aroma wangi daun pandan dan kayu manis berpadu ubi ungu antioksidan tinggi, dimasak memakai susu oat rendah kolesterol.',
        ingredients: ['1 buah Pisang Kepok kukus potong', '1 buah Ubi Ungu potong dadu', '200 ml Susu Oat tawar', '1 lembar Daun Pandan simpul', '1 batang Kayu Manis kecil', '1 sdm Gula Aren organik'],
        tips: 'Ubi ungu kaya antosianin alami yang membantu melindungi sel-sel tubuh dari stres oksidatif selama Ramadhan.'
    },
    {
        id: 'm13',
        title: 'Es Cincau Hijau Organik Kuah Santan Encer & Gula Aren',
        category: 'takjil',
        calories: 160,
        protein: 2,
        carbs: 34,
        fiber: 7,
        tag: 'Pereda Panas Dalam Alami',
        description: 'Cincau hijau perasan daun segar asli nusantara, berkhasiat menyejukkan tenggorokan kering dan meredakan asam lambung.',
        ingredients: ['1 mangkuk Cincau Hijau segar potong kotak', '150 ml Santan Encer kelapa asli rebus pandan', '1.5 sdm Gula Aren cair organik', 'Es batu kristal'],
        tips: 'Klorofil alami pada daun cincau hijau memiliki sifat alkali yang menetralkan keasaman saluran cerna.'
    },
    {
        id: 'm14',
        title: 'Air Nabeez (Infused Water Kurma Sunnah Rasulullah)',
        category: 'takjil',
        calories: 95,
        protein: 1,
        carbs: 23,
        fiber: 2,
        tag: 'Sunnah Nabawiyah Murni',
        description: 'Minuman rendaman kurma semalaman sesuai tuntunan Rasulullah SAW; berkhasiat menyeimbangkan pH tubuh dan mengembalikan energi instan.',
        ingredients: ['3-5 butir Kurma Ajwa / Sukari', '350 ml Air mineral bersih dalam botol/gelas tertutup'],
        tips: 'Rendam kurma di suhu ruang tertutup selama 8-12 jam. Minum air rendamannya dan santap kurmanya yang sudah sangat lembut.'
    },

    // --- IFTAR / BUKA PUASA SEIMBANG (7 MENU) ---
    {
        id: 'm15',
        title: 'Sup Ikan Kakap Bening Jahe & Tumis Jamur Tiram',
        category: 'iftar',
        calories: 390,
        protein: 32,
        carbs: 35,
        fiber: 5,
        tag: 'Hangat & Rendah Lemak',
        description: 'Menu makan malam pasca tarawih yang hangat di lambung, kaya Omega-3 dan asam amino untuk regenerasi sel di malam hari.',
        ingredients: ['150g Ikan Kakap/Gurame fillet', 'Kuah rempah jahe, sereh, daun jeruk, tomat hijau', '1 centong nasi putih/coklat', '1 piring kecil tumis jamur tiram bawang putih'],
        tips: 'Aroma jahe dan sereh meredakan begah pada perut setelah seharian berpuasa.'
    },
    {
        id: 'm16',
        title: 'Salad Quinoa Tempe Bakar & Dressing Wijen Sangrai',
        category: 'iftar',
        calories: 360,
        protein: 18,
        carbs: 40,
        fiber: 7,
        tag: 'Plant-Based High Fiber',
        description: 'Pilihan makan malam vegan/vegetarian yang kaya antioksidan, serat prebiotik, dan asam folat tinggi untuk kesehatan muslimah.',
        ingredients: ['1/2 cup Quinoa masak', '100g Tempe bakar rempah', 'Selada romaine, edamame, cherry tomato', '1 sdm Sesame dressing'],
        tips: 'Membantu menjaga berat badan tetap stabil dan terhindar dari kolesterol selama Ramadhan.'
    },
    {
        id: 'm17',
        title: 'Daging Sapi Tumis Brokoli Paprika & Nasi Merah',
        category: 'iftar',
        calories: 490,
        protein: 36,
        carbs: 45,
        fiber: 6,
        tag: 'Tinggi Zat Besi & Seng (Zinc)',
        description: 'Pilihan menu utama pencegah anemia dan letih lesu; kombinasi daging sapi tanpa lemak dan vitamin C brokoli memaksimalkan penyerapan zat besi.',
        ingredients: ['120g Daging Sapi has dalam iris tipis', '1 mangkuk Kuntum Brokoli kukus renyah', '1/2 buah Paprika merah iris', 'Saus tiram rendah natrium & bawang putih', '1 centong Nasi Merah'],
        tips: 'Masak brokoli sebentar saja (blanching) agar kandungan vitamin C dan sulforaphane anti-kanker tetap utuh.'
    },
    {
        id: 'm18',
        title: 'Ayam Panggang Bumbu Rujak Tanpa Kulit & Sayur Lodeh Tahu',
        category: 'iftar',
        calories: 460,
        protein: 34,
        carbs: 42,
        fiber: 6,
        tag: 'Rempah Tradisional Rendah Lemak',
        description: 'Sensasi bumbu rujak khas Jawa Timur yang gurih pedas manis, disajikan dengan sayur labu siam dan tahu kaya protein nabati.',
        ingredients: ['1 potong Dada Ayam tanpa kulit bakar teflon bumbu rujak', '1 mangkuk Sayur Lodeh santan sangat encer (labu siam, daun melinjo, tahu)', '1 centong Nasi Beras Coklat'],
        tips: 'Panggang menggunakan wajan teflon dengan olesan tipis minyak kelapa murni (VCO) agar kalori tetap terkontrol.'
    },
    {
        id: 'm19',
        title: 'Gado-Gado Sayur Segar Telur Rebus & Saus Kacang Oven',
        category: 'iftar',
        calories: 410,
        protein: 20,
        carbs: 46,
        fiber: 9,
        tag: 'Kaya Aneka Serat Nabati',
        description: 'Pesta serat aneka sayuran rebus (selada, pare, labu, tauge, kentang kukus) dipadukan telur rebus dan bumbu kacang gurih tanpa minyak goreng.',
        ingredients: ['1 butir Telur Ayam rebus', '1 potong Tempe & Tahu kukus bumbu ketumbar', 'Kangkung, tauge, kubis, kentang rebus', 'Saus kacang tanah/mete sangrai oven'],
        tips: 'Mengganti kacang goreng dengan kacang sangrai/oven memangkas lebih dari 40% kalori lemak jenuh pada bumbu gado-gado.'
    },
    {
        id: 'm20',
        title: 'Pepes Tahu Jamur Kemangi, Teri Nasi & Lalapan Segar',
        category: 'iftar',
        calories: 370,
        protein: 26,
        carbs: 38,
        fiber: 7,
        tag: 'Kaya Kalsium Alami Tulang',
        description: 'Menu rumahan nikmat yang sangat kaya kalsium alami dari teri nasi dan tahu, menjaga kepadatan tulang dan kebugaran muslimah.',
        ingredients: ['2 bungkus Pepes Tahu jamur tiram daun kemangi kukus', '2 sdm Ikan Teri Nasi tawar sangrai garing', '1 centong Nasi Beras Merah', 'Lalap timun, selada, tomat segar'],
        tips: 'Ikan teri nasi dimakan utuh dengan tulangnya, menjadi sumber kalsium dan fosfor alami yang paling mudah diserap tubuh.'
    },
    {
        id: 'm21',
        title: 'Shabu-Shabu Rumahan: Daging Sukiyaki, Tofu & Sawi Putih',
        category: 'iftar',
        calories: 420,
        protein: 32,
        carbs: 34,
        fiber: 6,
        tag: 'Segar Berkuah Hangat Pasca Tarawih',
        description: 'Hidangan rebusan praktis pasca sholat tarawih; kuah kaldu rempah bening serai dan jeruk nipis yang melegakan pernapasan dan menghangatkan badan.',
        ingredients: ['80g Daging Sapi Sukiyaki tipis (low fat)', '1 roll Tofu telur sutra potong bulat', 'Aneka jamur (enoki, shimeji), sawi putih, pakcoy', 'Kuah kaldu bening serai, daun jeruk, jeruk nipis', '50g Bihun jagung rebus'],
        tips: 'Metode rebusan tanpa minyak (hotpot) menjaga keutuhan mikronutrien sayuran dan protein tanpa beban lemak jenuh.'
    }
];

// Daftar Amalan Mutaba'ah Harian
const DAILY_DEEDS = [
    { id: 'fardhu', label: 'Sholat 5 Waktu Tepat Waktu', detail: 'Subuh, Dzuhur, Ashar, Maghrib, Isya di awal waktu' },
    { id: 'tarawih', label: 'Sholat Tarawih & Witir', detail: 'Berjamaah di masjid atau khusyuk di rumah' },
    { id: 'tilawah', label: 'Tilawah Sesuai Target Harian', detail: 'Membaca mushaf sesuai target Khatam Planner' },
    { id: 'sedekah', label: 'Sedekah Subuh / Ramadhan', detail: 'Infaq ifthar, tebar berkah, atau sedekah digital' },
    { id: 'dzikir_pagi', label: 'Dzikir Pagi', detail: 'Menjaga benteng dzikir Al-Ma\'tsurat ba\'da Subuh' },
    { id: 'dzikir_petang', label: 'Dzikir Petang', detail: 'Mengingat Allah menjelang waktu berbuka puasa' },
    { id: 'dhuha', label: 'Sholat Sunnah Dhuha', detail: 'Minimal 2 rakaat di pagi hari penjemput rezeki' },
    { id: 'tadabbur', label: 'Tadabbur 1 Ayat Al-Qur\'an', detail: 'Membaca terjemah & tafsir singkat 1 ayat inspiratif' }
];

// Interface & Daftar Sunnah-Sunnah Utama Ramadhan
export interface SunnahRamadhan {
    id: string;
    title: string;
    category: 'sahur_buka' | 'ibadah' | 'akhlak' | 'sedekah' | 'sepuluh_terakhir';
    categoryLabel: string;
    dalil: string;
    source: string;
    arabic?: string;
    latin?: string;
    translation?: string;
    tips: string;
    fadhilah: string;
}

const SUNNAH_LIST: SunnahRamadhan[] = [
    {
        id: 's1',
        title: 'Mengakhirkan Santap Sahur',
        category: 'sahur_buka',
        categoryLabel: 'Sahur & Berbuka',
        dalil: 'Manusia senantiasa berada dalam kebaikan selama mereka menyegerakan berbuka dan mengakhirkan sahur.',
        source: 'HR. Ahmad No. 21312 (Shahih)',
        tips: 'Makan sahur sekitar 30–45 menit sebelum waktu imsak/adzan Subuh agar pasokan energi tubuh terjaga lebih optimal.',
        fadhilah: 'Mendapatkan keberkahan makanan sahur serta limpahan shalawat dari Allah Ta\'ala dan para malaikat-Nya.'
    },
    {
        id: 's2',
        title: 'Menyegerakan Berbuka Puasa (Ta\'jil)',
        category: 'sahur_buka',
        categoryLabel: 'Sahur & Berbuka',
        dalil: 'Manusia senantiasa dalam kebaikan selama mereka menyegerakan berbuka puasa.',
        source: 'HR. Bukhari No. 1957 & Muslim No. 1098',
        tips: 'Segera batalkan puasa begitu adzan Maghrib berkumandang, dahulukan seteguk air putih dan kurma sebelum sholat.',
        fadhilah: 'Menghidupkan kemurnian sunnah Nabi SAW dan menjauhkan dari sikap ghuluw (mempersulit diri) dalam beragama.'
    },
    {
        id: 's3',
        title: 'Membaca Doa Shahih Saat Berbuka',
        category: 'sahur_buka',
        categoryLabel: 'Sahur & Berbuka',
        arabic: 'ذَهَبَ الظَّمَأُ وَابْتَلَّتِ الْعُرُوقُ وَثَبَتَ الأَجْرُ إِنْ شَاءَ اللَّهُ',
        latin: 'Dzahabazh zhama\'u wabtallatil \'uruuqu wa tsabatal ajru in syaa Allah',
        translation: 'Telah hilang rasa haus, telah basah urat-urat, dan telah pasti pahala, insya Allah.',
        dalil: 'Rasulullah SAW apabila telah berbuka puasa membaca doa ini.',
        source: 'HR. Abu Dawud No. 2357 & An-Nasa\'i (Hasan)',
        tips: 'Ucapkan basmalah, nikmati tegukan air/kurma pertama, lalu lafadzkan doa penuh syukur ini dengan khusyuk.',
        fadhilah: 'Waktu berbuka adalah salah satu momentum mustajab di mana doa orang yang berpuasa tidak akan tertolak.'
    },
    {
        id: 's4',
        title: 'Berbuka dengan Kurma Basah, Kering, atau Air',
        category: 'sahur_buka',
        categoryLabel: 'Sahur & Berbuka',
        dalil: 'Nabi SAW biasa berbuka dengan beberapa ruthab (kurma basah) sebelum sholat. Jika tidak ada, dengan tamr (kurma kering). Jika tidak ada, dengan beberapa teguk air.',
        source: 'HR. Abu Dawud No. 2356 & Tirmidzi No. 696',
        tips: 'Awali dengan 3 butir kurma (bilangan ganjil) yang kaya fruktosa alami dan elektrolit pemulih tenaga seluler.',
        fadhilah: 'Mengikuti tata cara sunnah pangan terbaik yang membersihkan sistem pencernaan pasca berpuasa.'
    },
    {
        id: 's5',
        title: 'Memberi Makan Orang Berbuka (Ith\'am ath-Tha\'am)',
        category: 'sedekah',
        categoryLabel: 'Sedekah & Sosial',
        dalil: 'Siapa yang memberi makan orang yang berpuasa, maka baginya pahala seperti orang itu tanpa mengurangi pahala orang yang berpuasa sedikit pun.',
        source: 'HR. Tirmidzi No. 807 & Ibnu Majah No. 1746 (Shahih)',
        tips: 'Siapkan paket takjil berkah untuk jamaah musholla, tetangga sekitar, santriwati, atau musafir di jalan.',
        fadhilah: 'Memperoleh pahala puasa orang lain yang kita berikan makan secara utuh berlipat ganda.'
    },
    {
        id: 's6',
        title: 'Menjaga Lisan & Menahan Diri dari Ghibah/Debat',
        category: 'akhlak',
        categoryLabel: 'Hati & Akhlak',
        dalil: 'Jika salah seorang sedang berpuasa, janganlah berkata kotor dan berbuat gaduh. Jika ada orang yang mencelanya, hendaklah ia berkata: "Inni sho\'im" (Aku sedang berpuasa).',
        source: 'HR. Bukhari No. 1894 & Muslim No. 1151',
        tips: 'Hindari perbincangan toxic di grup chat, tunda perdebatan media sosial, dan sibukkan lisan dengan zikir penyejuk hati.',
        fadhilah: 'Menjaga integritas pahala puasa agar tidak hangus sia-sia hanya menjadi rasa lapar dan dahaga semata.'
    },
    {
        id: 's7',
        title: 'Memperbanyak Tadarus & Khatam Al-Qur\'an',
        category: 'ibadah',
        categoryLabel: 'Ibadah & Al-Qur\'an',
        dalil: 'Malaikat Jibril AS senantiasa menemui Nabi SAW pada setiap malam Ramadhan untuk bertadarus Al-Qur\'an bersama beliau.',
        source: 'HR. Bukhari No. 6 & Muslim No. 2308',
        tips: 'Gunakan fitur Khatam Planner di halaman ini untuk membagi target bacaan lembar/halaman ba\'da sholat fardhu.',
        fadhilah: 'Al-Qur\'an dan ibadah puasa akan datang menjadi syafaat penolong di hadapan Allah pada hari kiamat.'
    },
    {
        id: 's8',
        title: 'Menghidupkan Malam dengan Tarawih & Witir (Qiyam)',
        category: 'ibadah',
        categoryLabel: 'Ibadah & Al-Qur\'an',
        dalil: 'Barangsiapa mendirikan sholat malam di bulan Ramadhan karena iman dan mengharap pahala, diampuni dosa-dosanya yang telah lalu.',
        source: 'HR. Bukhari No. 37 & Muslim No. 759',
        tips: 'Sholat tarawih berjamaah di masjid atau khusyuk di rumah, dan sempurnakan bersama witir hingga selesai.',
        fadhilah: 'Dicatat pahala sholat semalam suntuk jika sholat bersama imam dan diampuni dosa-dosa terdahulu.'
    },
    {
        id: 's9',
        title: 'Memperbanyak Sedekah & Kedermawanan',
        category: 'sedekah',
        categoryLabel: 'Sedekah & Sosial',
        dalil: 'Rasulullah SAW adalah orang yang paling dermawan, dan beliau lebih dermawan lagi di bulan Ramadhan melebihi angin yang berhembus kencang.',
        source: 'HR. Bukhari No. 3220',
        tips: 'Sisihkan sedekah subuh harian secara konsisten, wakaf mushaf Al-Qur\'an, atau infaq ke program dakwah binaan muslimah.',
        fadhilah: 'Pahala amal kebaikan dilipatgandakan dan sedekah menjadi naungan penyejuk di padang mahsyar.'
    },
    {
        id: 's10',
        title: 'I\'tikaf di 10 Malam Terakhir & Doa Lailatul Qadar',
        category: 'sepuluh_terakhir',
        categoryLabel: '10 Malam Terakhir',
        arabic: 'اللَّهُمَّ إِنَّكَ عَفُوٌّ تُحِبُّ الْعَفْوَ فَاعْفُ عَنِّي',
        latin: 'Allahumma innaka \'afuwwun tuhibbul \'afwa fa\'fu \'annii',
        translation: 'Ya Allah, sesungguhnya Engkau Maha Pemaaf, mencintai kemaafan, maka maafkanlah aku.',
        dalil: 'Rasulullah SAW beri\'tikaf pada sepuluh hari terakhir bulan Ramadhan dan bersungguh-sungguh mencari malam Lailatul Qadar.',
        source: 'HR. Tirmidzi No. 3513 (Shahih) & Bukhari No. 2026',
        tips: 'Tingkatkan kesungguhan ibadah di malam-malam ganjil (21, 23, 25, 27, 29) dengan memperbanyak doa ampunan ini.',
        fadhilah: 'Beribadah pada malam Lailatul Qadar bernilai lebih baik dan lebih mulia daripada beribadah seribu bulan.'
    },
    {
        id: 's11',
        title: 'Memperbanyak Istighfar di Waktu Sahar (Jelang Subuh)',
        category: 'ibadah',
        categoryLabel: 'Ibadah & Al-Qur\'an',
        dalil: 'Dan pada akhir-akhir malam mereka memohon ampunan (beristighfar) kepada Allah.',
        source: 'QS. Adz-Dzariyat: 18 & QS. Ali \'Imran: 17',
        tips: 'Sisihkan 10-15 menit sebelum waktu imsak untuk mengangkat tangan, memperbanyak istighfar, dan memanjatkan doa hajat.',
        fadhilah: 'Waktu sahar adalah saat Allah turun ke langit dunia mengabulkan permohonan hamba yang berdoa.'
    },
    {
        id: 's12',
        title: 'Mandi Besar Sebelum Fajar Bagi yang Berhadats',
        category: 'sahur_buka',
        categoryLabel: 'Sahur & Berbuka',
        dalil: 'Aisyah RA menceritakan bahwa Nabi SAW pernah mendapati waktu fajar dalam keadaan junub, lalu beliau mandi dan berpuasa.',
        source: 'HR. Bukhari No. 1926 & Muslim No. 1109',
        tips: 'Bagi muslimah yang telah suci dari haidh sebelum subuh, segerakan mandi sebelum adzan subuh agar menyambut puasa dalam kesucian.',
        fadhilah: 'Mengawali ibadah puasa dalam keadaan fitrah, suci, dan siap menyempurnakan sholat Subuh tepat waktu.'
    }
];

export const RamadhanHub: React.FC = () => {
    // 1. STATE KOTA & WAKTU SHOLAT
    const [selectedCity, setSelectedCity] = useState<string>('jakarta');
    const [currentTime, setCurrentTime] = useState<Date>(new Date());

    // 2. STATE KHATAM PLANNER
    const [targetKhatam, setTargetKhatam] = useState<number>(1);
    const [targetDays, setTargetDays] = useState<number>(30);
    const [calcMode, setCalcMode] = useState<'page' | 'sheet' | 'ain'>('page');
    const [completedJuz, setCompletedJuz] = useState<number[]>(() => {
        try {
            const saved = localStorage.getItem('khazanah_ramadhan_completed_juz');
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });

    // 3. STATE MUTABA'AH YAUMIYAH
    const todayKey = new Date().toISOString().slice(0, 10);
    const [mutabaahState, setMutabaahState] = useState<Record<string, boolean>>(() => {
        try {
            const saved = localStorage.getItem(`khazanah_mutabaah_${todayKey}`);
            return saved ? JSON.parse(saved) : {};
        } catch {
            return {};
        }
    });

    // 4. STATE WATER TRACKER (ATURAN 2-4-2)
    const [waterGlasses, setWaterGlasses] = useState<number>(() => {
        try {
            const saved = localStorage.getItem(`khazanah_water_${todayKey}`);
            return saved ? Number(saved) : 0;
        } catch {
            return 0;
        }
    });

    // 5. STATE MENU FILTER
    const [menuFilter, setMenuFilter] = useState<'all' | 'sahur' | 'takjil' | 'iftar'>('all');
    const [menuSearch, setMenuSearch] = useState<string>('');
    const [selectedMenuDetail, setSelectedMenuDetail] = useState<HealthyMenu | null>(null);

    // 6. STATE COUNTDOWN TAB & SUNNAH
    const [countdownTab, setCountdownTab] = useState<'ramadhan' | 'daily'>('ramadhan');
    const [sunnahFilter, setSunnahFilter] = useState<'all' | 'sahur_buka' | 'ibadah' | 'akhlak' | 'sedekah' | 'sepuluh_terakhir'>('all');
    const [practicedSunnah, setPracticedSunnah] = useState<Record<string, boolean>>(() => {
        try {
            const saved = localStorage.getItem(`khazanah_sunnah_${todayKey}`);
            return saved ? JSON.parse(saved) : {};
        } catch {
            return {};
        }
    });

    // Kalender Ramadhan Terdekat (Hisab Astronomis)
    const nextRamadhan = useMemo(() => {
        const now = currentTime;
        const milestones = [
            { year: 1446, targetDate: new Date(2025, 2, 1, 0, 0, 0), displayDate: '1 Maret 2025' },
            { year: 1447, targetDate: new Date(2026, 1, 18, 0, 0, 0), displayDate: '18 Februari 2026' },
            { year: 1448, targetDate: new Date(2027, 1, 8, 0, 0, 0), displayDate: '8 Februari 2027' },
            { year: 1449, targetDate: new Date(2028, 0, 28, 0, 0, 0), displayDate: '28 Januari 2028' },
        ];
        return milestones.find(m => now < m.targetDate) || milestones[milestones.length - 1];
    }, [currentTime]);

    const ramadhanTarget = nextRamadhan.targetDate;

    // Tanggal Hijriah Hari Ini (Anti-bug mobile browser ICU)
    const todayHijri = useMemo(() => getHijriDate(currentTime), [currentTime]);

    // Hitung Mundur Hari Menuju 1 Ramadhan
    const ramadhanCountdown = useMemo(() => {
        const diff = ramadhanTarget.getTime() - currentTime.getTime();
        if (diff <= 0) {
            return { days: 0, hours: 0, minutes: 0, seconds: 0, isToday: true };
        }
        return {
            days: Math.floor(diff / (1000 * 60 * 60 * 24)),
            hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
            minutes: Math.floor((diff / (1000 * 60)) % 60),
            seconds: Math.floor((diff / 1000) % 60),
            isToday: false
        };
    }, [currentTime, ramadhanTarget]);

    const handleCopyDoa = (text: string, title: string) => {
        navigator.clipboard.writeText(text);
        Swal.fire({
            icon: 'success',
            title: 'Doa Berhasil Disalin!',
            text: `Lafadz ${title} telah disalin ke clipboard Anda.`,
            timer: 1800,
            showConfirmButton: false
        });
    };

    const toggleSunnahPractice = (sunnahId: string) => {
        setPracticedSunnah((prev) => {
            const updated = { ...prev, [sunnahId]: !prev[sunnahId] };
            localStorage.setItem(`khazanah_sunnah_${todayKey}`, JSON.stringify(updated));
            return updated;
        });
    };

    // Live clock update tiap detik
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Simpan progres Juz ke localStorage
    const toggleJuz = (juzNumber: number) => {
        setCompletedJuz((prev) => {
            const updated = prev.includes(juzNumber)
                ? prev.filter((j) => j !== juzNumber)
                : [...prev, juzNumber];
            localStorage.setItem('khazanah_ramadhan_completed_juz', JSON.stringify(updated));
            return updated;
        });
    };

    // Reset Juz
    const resetJuzProgress = () => {
        Swal.fire({
            title: 'Mulai Ulang Progres Juz?',
            text: 'Ceklis 30 Juz akan direset ke awal. Anda dapat menyusun target baru.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#0F5B30',
            cancelButtonColor: '#9CA3AF',
            confirmButtonText: 'Ya, Reset',
            cancelButtonText: 'Batal'
        }).then((res) => {
            if (res.isConfirmed) {
                setCompletedJuz([]);
                localStorage.removeItem('khazanah_ramadhan_completed_juz');
                Swal.fire({
                    icon: 'success',
                    title: 'Progres Direset',
                    timer: 1500,
                    showConfirmButton: false
                });
            }
        });
    };

    // Toggle Mutaba'ah
    const toggleMutabaah = (deedId: string) => {
        setMutabaahState((prev) => {
            const updated = { ...prev, [deedId]: !prev[deedId] };
            localStorage.setItem(`khazanah_mutabaah_${todayKey}`, JSON.stringify(updated));
            return updated;
        });
    };

    // Toggle Water Glass
    const handleGlassClick = (index: number) => {
        // Jika klik gelas yang sudah aktif paling akhir, kurangi 1
        const newCount = index + 1 === waterGlasses ? index : index + 1;
        setWaterGlasses(newCount);
        localStorage.setItem(`khazanah_water_${todayKey}`, String(newCount));
    };

    // Hitung Countdown ke waktu terdekat (Maghrib / Imsak)
    const schedule = CITY_SCHEDULES[selectedCity] || CITY_SCHEDULES.jakarta;

    const nextMilestone = useMemo(() => {
        const now = currentTime;
        const [imsakH, imsakM] = schedule.imsak.split(':').map(Number);
        const [maghribH, maghribM] = schedule.maghrib.split(':').map(Number);

        const targetImsak = new Date(now);
        targetImsak.setHours(imsakH, imsakM, 0, 0);

        const targetMaghrib = new Date(now);
        targetMaghrib.setHours(maghribH, maghribM, 0, 0);

        // Jika sebelum imsak hari ini -> countdown ke Imsak
        if (now < targetImsak) {
            const diff = targetImsak.getTime() - now.getTime();
            return {
                title: 'Menuju Waktu Imsak',
                targetTime: schedule.imsak,
                hours: Math.floor(diff / (1000 * 60 * 60)),
                minutes: Math.floor((diff / (1000 * 60)) % 60),
                seconds: Math.floor((diff / 1000) % 60),
                isImsak: true
            };
        }

        // Jika antara imsak dan maghrib -> countdown ke Buka Puasa (Maghrib)
        if (now >= targetImsak && now < targetMaghrib) {
            const diff = targetMaghrib.getTime() - now.getTime();
            return {
                title: 'Menuju Berbuka Puasa (Maghrib)',
                targetTime: schedule.maghrib,
                hours: Math.floor(diff / (1000 * 60 * 60)),
                minutes: Math.floor((diff / (1000 * 60)) % 60),
                seconds: Math.floor((diff / 1000) % 60),
                isImsak: false
            };
        }

        // Jika setelah maghrib -> countdown ke Imsak esok hari
        const tomorrowImsak = new Date(targetImsak);
        tomorrowImsak.setDate(tomorrowImsak.getDate() + 1);
        const diff = tomorrowImsak.getTime() - now.getTime();
        return {
            title: 'Menuju Waktu Imsak Besok',
            targetTime: schedule.imsak,
            hours: Math.floor(diff / (1000 * 60 * 60)),
            minutes: Math.floor((diff / (1000 * 60)) % 60),
            seconds: Math.floor((diff / 1000) % 60),
            isImsak: true
        };
    }, [currentTime, schedule]);

    // Kalkulasi Khatam Planner
    // Total: 604 Halaman, 302 Lembar, 554 Ruku' ('Ain)
    const khatamCalc = useMemo(() => {
        const totalPages = 604 * targetKhatam;
        const totalSheets = 302 * targetKhatam;
        const totalAins = 554 * targetKhatam;

        const pagesPerDay = totalPages / targetDays;
        const pagesPerPrayer = pagesPerDay / 5;

        const sheetsPerDay = totalSheets / targetDays;
        const sheetsPerPrayer = sheetsPerDay / 5;

        const ainsPerDay = totalAins / targetDays;
        const ainsPerPrayer = ainsPerDay / 5;

        return {
            pagesPerDay: Math.ceil(pagesPerDay * 10) / 10,
            pagesPerPrayer: Math.ceil(pagesPerPrayer * 10) / 10,
            sheetsPerDay: Math.ceil(sheetsPerDay * 10) / 10,
            sheetsPerPrayer: Math.ceil(sheetsPerPrayer * 10) / 10,
            ainsPerDay: Math.ceil(ainsPerDay * 10) / 10,
            ainsPerPrayer: Math.ceil(ainsPerPrayer * 10) / 10
        };
    }, [targetKhatam, targetDays]);

    // Estimasi waktu tilawah per waktu sholat menyesuaikan mode & porsi bacaan
    const prayerReadingEstimate = useMemo(() => {
        let portionText = `${khatamCalc.pagesPerPrayer} halaman`;
        let equivPages = khatamCalc.pagesPerPrayer;

        if (calcMode === 'sheet') {
            portionText = `${khatamCalc.sheetsPerPrayer} lembar`;
            equivPages = khatamCalc.sheetsPerPrayer * 2; // 1 lembar = 2 halaman
        } else if (calcMode === 'ain') {
            portionText = `${khatamCalc.ainsPerPrayer} 'ain`;
            equivPages = khatamCalc.ainsPerPrayer * 1.09; // 554 'ain dalam 604 halaman (~1.09 halaman per 'ain)
        }

        // Estimasi waktu baca tartil tajwid: ~1.9 - 2.45 menit per halaman
        const minMins = Math.max(1, Math.round(equivPages * 1.9));
        const maxMins = Math.max(minMins + 1, Math.round(equivPages * 2.45));

        return {
            portionText,
            minMins,
            maxMins,
            isLight: maxMins <= 15
        };
    }, [khatamCalc, calcMode]);

    // Persentase Juz Selesai
    const juzPercentage = Math.round((completedJuz.length / 30) * 100);

    // Persentase Mutaba'ah Hari Ini
    const completedDeedsCount = Object.values(mutabaahState).filter(Boolean).length;
    const mutabaahPercentage = Math.round((completedDeedsCount / DAILY_DEEDS.length) * 100);

    // Filter Menu Sehat (Kategori + Pencarian)
    const filteredMenus = useMemo(() => {
        const query = menuSearch.trim().toLowerCase();
        return HEALTHY_MENUS.filter((m) => {
            const matchesCategory = menuFilter === 'all' || m.category === menuFilter;
            const matchesSearch = !query ||
                m.title.toLowerCase().includes(query) ||
                m.tag.toLowerCase().includes(query) ||
                m.description.toLowerCase().includes(query) ||
                m.ingredients.some(ing => ing.toLowerCase().includes(query));
            return matchesCategory && matchesSearch;
        });
    }, [menuFilter, menuSearch]);

    // Filter Sunnah
    const filteredSunnahs = useMemo(() => {
        if (sunnahFilter === 'all') return SUNNAH_LIST;
        return SUNNAH_LIST.filter((s) => s.category === sunnahFilter);
    }, [sunnahFilter]);

    const completedSunnahCount = Object.values(practicedSunnah).filter(Boolean).length;

    return (
        <div className="min-h-screen bg-[#FDFBF7] text-gray-800 font-sans selection:bg-emerald/20 selection:text-emerald">
            {/* 1. HERO SECTION & LIVE COUNTDOWN */}
            <AnimatedSection isHero={true} className="relative overflow-hidden bg-gradient-to-b from-[#07381E] via-[#0B4A28] to-[#0F5B30] text-white pt-28 pb-20 px-4 sm:px-6 lg:px-8">
                {/* Background Islamic Ornaments */}
                <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#F3CF7A_1px,transparent_1px)] [background-size:24px_24px] pointer-events-none" />
                <div className="absolute -top-32 -right-32 w-96 h-96 bg-gold/15 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-emerald-300/10 rounded-full blur-3xl pointer-events-none" />

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
                        {/* Title & Tagline */}
                        <div className="text-center lg:text-left max-w-2xl space-y-5">
                            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2.5">
                                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-gold text-xs font-bold tracking-wide uppercase shadow-sm">
                                    <span>Spesial Tematik Khazanah Muslimah</span>
                                </div>
                                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-emerald-100 text-xs font-bold tracking-wide shadow-sm">
                                    <Calendar size={13} className="text-gold" />
                                    <span>{todayHijri.formatted}</span>
                                </div>
                                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/20 backdrop-blur-md border border-gold/40 text-gold text-xs font-bold tracking-wide shadow-sm">
                                    <Moon size={13} className="text-gold fill-gold/30" />
                                    <span>Menuju 1 Ramadhan: {ramadhanCountdown.days} Hari Lagi</span>
                                </div>
                            </div>

                            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black font-outfit tracking-tight leading-tight text-white">
                                Ramadhan Hub & <span className="bg-gradient-to-r from-gold via-amber-200 to-white bg-clip-text text-transparent">Khatam Planner</span>
                            </h1>

                            <p className="text-sm sm:text-base text-emerald-100/90 font-medium leading-relaxed">
                                Ruang dedikasi muslimah untuk menyambut dan mengisi bulan suci Ramadhan {nextRamadhan.year} H. Pantau hitung mundur menuju puasa, target tilawah khatam Al-Qur'an, amalan sunnah, nutrisi sahur-berbuka, serta mutaba'ah harian.
                            </p>

                            {/* Shortcut Badges */}
                            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 pt-2 text-xs font-semibold">
                                <a href="#khatam-planner" className="px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 transition-all flex items-center gap-1.5">
                                    <BookOpen size={14} className="text-gold" />
                                    <span>Khatam Planner</span>
                                </a>
                                <a href="#sunnah-ramadhan" className="px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 transition-all flex items-center gap-1.5">
                                    <Sparkles size={14} className="text-gold" />
                                    <span>Sunnah Puasa</span>
                                </a>
                                <a href="#jadwal-imsakiyah" className="px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 transition-all flex items-center gap-1.5">
                                    <Clock size={14} className="text-gold" />
                                    <span>Jadwal Imsakiyah</span>
                                </a>
                                <a href="#nutrisi-menu" className="px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 transition-all flex items-center gap-1.5">
                                    <Apple size={14} className="text-gold" />
                                    <span>Nutrisi Sehat</span>
                                </a>
                                <a href="#mutabaah" className="px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 transition-all flex items-center gap-1.5">
                                    <CheckCircle2 size={14} className="text-gold" />
                                    <span>Mutaba'ah Harian</span>
                                </a>
                            </div>
                        </div>

                        {/* Dual Mode Countdown Widget Card */}
                        <div className="w-full lg:w-auto lg:min-w-[450px] bg-white/10 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-white/20 shadow-2xl shadow-emerald-950/40 relative">
                            {/* Toggle Tabs Countdown */}
                            <div className="grid grid-cols-2 gap-1.5 p-1 bg-black/25 rounded-2xl mb-6 border border-white/10 text-xs font-bold">
                                <button
                                    onClick={() => setCountdownTab('ramadhan')}
                                    className={`py-2 px-3 rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 ${countdownTab === 'ramadhan'
                                        ? 'bg-gradient-to-r from-gold to-amber-400 text-gray-900 shadow-sm font-black'
                                        : 'text-emerald-100 hover:text-white'
                                        }`}
                                >
                                    <Moon size={14} />
                                    <span>Menuju 1 Ramadhan</span>
                                </button>
                                <button
                                    onClick={() => setCountdownTab('daily')}
                                    className={`py-2 px-3 rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 ${countdownTab === 'daily'
                                        ? 'bg-gradient-to-r from-gold to-amber-400 text-gray-900 shadow-sm font-black'
                                        : 'text-emerald-100 hover:text-white'
                                        }`}
                                >
                                    <Clock size={14} />
                                    <span>Imsak & Berbuka</span>
                                </button>
                            </div>

                            {/* TAB 1: COUNTDOWN HARI MENUJU 1 RAMADHAN */}
                            {countdownTab === 'ramadhan' ? (
                                <div className="space-y-6">
                                    <div className="text-center space-y-1">
                                        <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-gold/90">
                                            Hitung Mundur Bulan Suci
                                        </span>
                                        <h3 className="text-2xl font-black font-outfit text-white">
                                            1 Ramadhan {nextRamadhan.year} H
                                        </h3>
                                        <p className="text-xs text-emerald-200 font-medium">
                                            Insya Allah ~{nextRamadhan.displayDate} (Hisab Astronomis)
                                        </p>
                                    </div>

                                    {/* 4 Kolom: Hari, Jam, Menit, Detik */}
                                    <div className="grid grid-cols-4 gap-2.5 text-center">
                                        <div className="bg-white/15 rounded-2xl p-3 border border-white/15 backdrop-blur-xs">
                                            <span className="block text-2xl sm:text-3xl font-black font-outfit text-gold tracking-tight">
                                                {ramadhanCountdown.days}
                                            </span>
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-200">Hari</span>
                                        </div>
                                        <div className="bg-white/10 rounded-2xl p-3 border border-white/10">
                                            <span className="block text-2xl sm:text-3xl font-black font-outfit text-white tracking-tight">
                                                {String(ramadhanCountdown.hours).padStart(2, '0')}
                                            </span>
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-200">Jam</span>
                                        </div>
                                        <div className="bg-white/10 rounded-2xl p-3 border border-white/10">
                                            <span className="block text-2xl sm:text-3xl font-black font-outfit text-white tracking-tight">
                                                {String(ramadhanCountdown.minutes).padStart(2, '0')}
                                            </span>
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-200">Menit</span>
                                        </div>
                                        <div className="bg-white/10 rounded-2xl p-3 border border-white/10">
                                            <span className="block text-2xl sm:text-3xl font-black font-outfit text-amber-300 tracking-tight">
                                                {String(ramadhanCountdown.seconds).padStart(2, '0')}
                                            </span>
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-200">Detik</span>
                                        </div>
                                    </div>

                                    {/* Quote Penyejuk Hati */}
                                    <div className="p-3.5 bg-emerald-950/40 rounded-2xl border border-white/10 text-center text-xs text-emerald-100 font-medium leading-relaxed">
                                        <p className="italic text-gold">"Allahumma ballighnaa Ramadhan..."</p>
                                        <p className="text-[11px] text-emerald-200/80 mt-0.5">Ya Allah, pertemukanlah kami dengan bulan suci Ramadhan dalam keadaan iman dan sehat.</p>
                                    </div>
                                </div>
                            ) : (
                                /* TAB 2: COUNTDOWN WAKTU SHOLAT & BERBUKA HARIAN */
                                <div>
                                    {/* City Selector */}
                                    <div className="flex items-center justify-between pb-3 border-b border-white/10 mb-5">
                                        <div className="flex items-center gap-2 text-xs font-bold text-emerald-100">
                                            <MapPin size={15} className="text-gold" />
                                            <span>Wilayah Kota:</span>
                                        </div>
                                        <select
                                            value={selectedCity}
                                            onChange={(e) => setSelectedCity(e.target.value)}
                                            className="bg-emerald-900/80 text-white font-bold text-xs py-1.5 px-3 rounded-xl border border-white/20 focus:outline-none focus:ring-2 focus:ring-gold/50 cursor-pointer"
                                        >
                                            {Object.values(CITY_SCHEDULES).map((c) => (
                                                <option key={c.id} value={c.id} className="bg-emerald-950 text-white">
                                                    {c.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Milestone Header */}
                                    <div className="text-center space-y-1 mb-5">
                                        <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-gold/90">
                                            {nextMilestone.title}
                                        </span>
                                        <div className="text-2xl font-black font-outfit text-white flex items-center justify-center gap-2">
                                            {nextMilestone.isImsak ? <Moon size={22} className="text-gold" /> : <Sun size={22} className="text-amber-400" />}
                                            <span>Pukul {nextMilestone.targetTime} WIB</span>
                                        </div>
                                    </div>

                                    {/* Live Countdown Clock Blocks */}
                                    <div className="grid grid-cols-3 gap-3 text-center mb-5">
                                        <div className="bg-white/10 rounded-2xl p-3 border border-white/10">
                                            <span className="block text-3xl font-black font-outfit text-gold tracking-tight">
                                                {String(nextMilestone.hours).padStart(2, '0')}
                                            </span>
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-200">Jam</span>
                                        </div>
                                        <div className="bg-white/10 rounded-2xl p-3 border border-white/10">
                                            <span className="block text-3xl font-black font-outfit text-white tracking-tight">
                                                {String(nextMilestone.minutes).padStart(2, '0')}
                                            </span>
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-200">Menit</span>
                                        </div>
                                        <div className="bg-white/10 rounded-2xl p-3 border border-white/10">
                                            <span className="block text-3xl font-black font-outfit text-amber-300 tracking-tight">
                                                {String(nextMilestone.seconds).padStart(2, '0')}
                                            </span>
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-200">Detik</span>
                                        </div>
                                    </div>

                                    {/* Mini Jadwal Baris */}
                                    <div className="grid grid-cols-4 gap-2 text-center text-[11px] font-semibold bg-emerald-950/40 rounded-2xl p-3 border border-white/10">
                                        <div>
                                            <span className="text-emerald-300/80 block text-[10px]">Imsak</span>
                                            <span className="font-bold text-white">{schedule.imsak}</span>
                                        </div>
                                        <div>
                                            <span className="text-emerald-300/80 block text-[10px]">Subuh</span>
                                            <span className="font-bold text-white">{schedule.subuh}</span>
                                        </div>
                                        <div>
                                            <span className="text-emerald-300/80 block text-[10px]">Maghrib</span>
                                            <span className="font-bold text-gold">{schedule.maghrib}</span>
                                        </div>
                                        <div>
                                            <span className="text-emerald-300/80 block text-[10px]">Isya</span>
                                            <span className="font-bold text-white">{schedule.isya}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </AnimatedSection>

            {/* 2. KHATAM PLANNER & CALCULATOR */}
            <AnimatedSection id="khatam-planner" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <div className="text-center max-w-3xl mx-auto mb-14 space-y-3">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald/10 text-emerald font-bold text-xs tracking-wider uppercase">
                        <BookOpen size={14} />
                        <span>Khatam Planner & Calculator</span>
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-black font-outfit text-gray-900 tracking-tight">
                        Rancang Target Khatam Al-Qur'an Ramadhan
                    </h2>
                    <p className="text-sm sm:text-base text-gray-600 font-medium leading-relaxed">
                        Kalkulasi presisi pembagian tilawah berdasarkan mushaf standar Madinah (30 Juz = 604 Halaman = 302 Lembar = 554 Ruku'). Sesuaikan target dengan ritme harian Anda.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    {/* Calculator Controls (Left Column) */}
                    <div className="lg:col-span-5 bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-100 space-y-6">
                        <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                            <h3 className="font-black font-outfit text-lg text-gray-900 flex items-center gap-2">
                                <span>Parameter Target</span>
                            </h3>
                            <span className="text-xs font-bold text-emerald bg-emerald-50 px-2.5 py-1 rounded-lg">
                                Otomatis Tersimpan
                            </span>
                        </div>

                        {/* Slider Target Khatam */}
                        <div className="space-y-3">
                            <div className="flex justify-between items-center text-xs font-bold">
                                <span className="text-gray-600">Berapa Kali Target Khatam?</span>
                                <span className="text-emerald text-sm font-black px-2.5 py-0.5 bg-emerald-50 rounded-md">
                                    {targetKhatam}x Khatam
                                </span>
                            </div>
                            <input
                                type="range"
                                min={1}
                                max={5}
                                step={1}
                                value={targetKhatam}
                                onChange={(e) => setTargetKhatam(Number(e.target.value))}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#0F5B30]"
                            />
                            <div className="flex justify-between text-[11px] text-gray-400 font-bold px-1">
                                <span>1x</span>
                                <span>2x</span>
                                <span>3x</span>
                                <span>4x</span>
                                <span>5x</span>
                            </div>
                        </div>

                        {/* Slider Durasi Hari */}
                        <div className="space-y-3">
                            <div className="flex justify-between items-center text-xs font-bold">
                                <span className="text-gray-600">Durasi Penyelesaian:</span>
                                <span className="text-emerald text-sm font-black px-2.5 py-0.5 bg-emerald-50 rounded-md">
                                    {targetDays} Hari
                                </span>
                            </div>
                            <input
                                type="range"
                                min={10}
                                max={30}
                                step={1}
                                value={targetDays}
                                onChange={(e) => setTargetDays(Number(e.target.value))}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#0F5B30]"
                            />
                            <div className="flex justify-between text-[11px] text-gray-400 font-bold px-1">
                                <span>10 Hari</span>
                                <span>15 Hari</span>
                                <span>20 Hari</span>
                                <span>30 Hari</span>
                            </div>
                        </div>

                        {/* Mode Pembagian */}
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-600 block">Metode Pembagian:</label>
                            <div className="grid grid-cols-3 gap-2">
                                <button
                                    onClick={() => setCalcMode('page')}
                                    className={`py-2 px-3 rounded-xl text-xs font-bold transition-all ${calcMode === 'page' ? 'bg-emerald text-white shadow-xs' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
                                >
                                    Halaman
                                </button>
                                <button
                                    onClick={() => setCalcMode('sheet')}
                                    className={`py-2 px-3 rounded-xl text-xs font-bold transition-all ${calcMode === 'sheet' ? 'bg-emerald text-white shadow-xs' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
                                >
                                    Lembar
                                </button>
                                <button
                                    onClick={() => setCalcMode('ain')}
                                    className={`py-2 px-3 rounded-xl text-xs font-bold transition-all ${calcMode === 'ain' ? 'bg-emerald text-white shadow-xs' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
                                >
                                    Ruku' ('Ain)
                                </button>
                            </div>
                        </div>

                        {/* Breakdown Hasil Per Hari & Per Waktu Sholat */}
                        <div className="p-5 rounded-2xl bg-emerald-50/60 border border-emerald-100 space-y-4">
                            <span className="text-[11px] font-bold text-emerald uppercase tracking-wider block">
                                Rekomendasi Alokasi Tilawah
                            </span>

                            <div className="grid grid-cols-2 gap-3 text-center">
                                <div className="bg-white rounded-xl p-3 border border-emerald-100/70 shadow-xs">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase">Per Hari</span>
                                    <div className="text-2xl font-black font-outfit text-emerald">
                                        {calcMode === 'page' && `${khatamCalc.pagesPerDay} Hal`}
                                        {calcMode === 'sheet' && `${khatamCalc.sheetsPerDay} Lembar`}
                                        {calcMode === 'ain' && `${khatamCalc.ainsPerDay} 'Ain`}
                                    </div>
                                    <span className="text-[10px] text-gray-500 font-medium">Dalam {targetDays} hari</span>
                                </div>

                                <div className="bg-white rounded-xl p-3 border border-emerald-100/70 shadow-xs">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase">Setiap Sholat Fardhu</span>
                                    <div className="text-2xl font-black font-outfit text-emerald">
                                        {calcMode === 'page' && `${khatamCalc.pagesPerPrayer} Hal`}
                                        {calcMode === 'sheet' && `${khatamCalc.sheetsPerPrayer} Lembar`}
                                        {calcMode === 'ain' && `${khatamCalc.ainsPerPrayer} 'Ain`}
                                    </div>
                                    <span className="text-[10px] text-gray-500 font-medium">5x waktu sholat</span>
                                </div>
                            </div>

                            <p className="text-[11px] text-emerald-900/80 font-medium leading-relaxed italic">
                                💡 Tip Muslimah: Membaca {prayerReadingEstimate.portionText} setiap selesai sholat fardhu{' '}
                                {prayerReadingEstimate.isLight ? (
                                    <>
                                        terasa sangat ringan dan membutuhkan waktu hanya sekitar{' '}
                                        <span className="font-bold text-emerald not-italic">{prayerReadingEstimate.minMins}–{prayerReadingEstimate.maxMins} menit</span>.
                                    </>
                                ) : (
                                    <>
                                        diestimasikan membutuhkan waktu sekitar{' '}
                                        <span className="font-bold text-emerald not-italic">{prayerReadingEstimate.minMins}–{prayerReadingEstimate.maxMins} menit</span>. Sisihkan waktu tenang setelah sholat agar tilawah tetap khusyuk dan target khatam tercapai.
                                    </>
                                )}
                            </p>
                        </div>
                    </div>

                    {/* Interactive 30 Juz Progress Tracker (Right Column) */}
                    <div className="lg:col-span-7 bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-100 space-y-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-100">
                            <div>
                                <h3 className="font-black font-outfit text-lg text-gray-900 flex items-center gap-2">
                                    <Award size={18} className="text-emerald" />
                                    <span>Peta Capaian 30 Juz Al-Qur'an</span>
                                </h3>
                                <p className="text-xs text-gray-500 font-medium">
                                    Klik nomor juz untuk menandai tilawah yang telah diselesaikan.
                                </p>
                            </div>

                            <div className="flex items-center gap-3">
                                <button
                                    onClick={resetJuzProgress}
                                    className="text-xs font-bold text-gray-400 hover:text-rose-600 transition-colors flex items-center gap-1 cursor-pointer"
                                >
                                    <RefreshCw size={13} />
                                    <span>Reset</span>
                                </button>
                                <span className="px-3 py-1 rounded-full bg-emerald text-white text-xs font-black shadow-xs">
                                    {completedJuz.length} / 30 Juz ({juzPercentage}%)
                                </span>
                            </div>
                        </div>

                        {/* Progress Bar Visual */}
                        <div className="space-y-2">
                            <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden p-0.5 border border-gray-200">
                                <div
                                    className="bg-gradient-to-r from-emerald to-emerald-400 h-full rounded-full transition-all duration-500 shadow-sm"
                                    style={{ width: `${juzPercentage}%` }}
                                />
                            </div>
                            <div className="flex justify-between items-center text-[11px] text-gray-400 font-bold">
                                <span>Mulai (Juz 1)</span>
                                <span>{juzPercentage === 100 ? '🎉 Alhamdulillah Khatam!' : `${30 - completedJuz.length} Juz tersisa`}</span>
                                <span>Khatam (Juz 30)</span>
                            </div>
                        </div>

                        {/* 30 Juz Interactive Grid */}
                        <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-10 gap-2.5">
                            {Array.from({ length: 30 }, (_, idx) => idx + 1).map((juzNum) => {
                                const isDone = completedJuz.includes(juzNum);
                                return (
                                    <button
                                        key={juzNum}
                                        onClick={() => toggleJuz(juzNum)}
                                        className={`p-2.5 rounded-2xl flex flex-col items-center justify-center transition-all cursor-pointer border ${isDone
                                            ? 'bg-emerald text-white border-emerald shadow-xs shadow-emerald/30 scale-102'
                                            : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-emerald/40 hover:bg-emerald-50/50'
                                            }`}
                                        title={`Juz ${juzNum} ${isDone ? '(Selesai)' : '(Belum Selesai)'}`}
                                    >
                                        <span className="text-[10px] font-bold uppercase tracking-tighter opacity-80">Juz</span>
                                        <span className="text-base font-black font-outfit">{juzNum}</span>
                                        {isDone ? (
                                            <Check size={12} className="text-gold mt-0.5" />
                                        ) : (
                                            <Circle size={10} className="text-gray-300 mt-0.5" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Motivational Note */}
                        <div className="p-4 rounded-2xl bg-amber-50/70 border border-amber-200 text-xs text-amber-950 font-medium flex items-start gap-3">
                            <span className="text-lg">🌿</span>
                            <div className="leading-relaxed">
                                <strong className="font-bold text-amber-900">Keberkahan Membaca Al-Qur'an:</strong> "Barangsiapa membaca satu huruf dari kitab Allah, maka baginya satu kebaikan dengan sepuluh kali lipatnya..." (HR. Tirmidzi). Konsistensi kecil yang istiqomah lebih dicintai Allah daripada banyak namun terputus-putus.
                            </div>
                        </div>
                    </div>
                </div>
            </AnimatedSection>

            {/* 3. SUNNAH-SUNNAH UTAMA SAAT BULAN PUASA */}
            <AnimatedSection id="sunnah-ramadhan" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <div className="text-center max-w-3xl mx-auto mb-14 space-y-3">
                    <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-gold/15 text-amber-900 font-bold text-xs tracking-wider uppercase border border-gold/30">
                        <span>Panduan Sunnah Rasulullah SAW</span>
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-black font-outfit text-gray-900 tracking-tight">
                        Sunnah-Sunnah Utama di Bulan Puasa
                    </h2>
                    <p className="text-sm sm:text-base text-gray-600 font-medium leading-relaxed">
                        Sempurnakan ibadah puasa dengan menghidupkan amalan sunnah Nabi Muhammad SAW. Raih pahala berlipat ganda, mulai dari adab sahur, doa berbuka, hingga ikhtiar memburu Lailatul Qadar.
                    </p>

                    {/* Progress Badge Amalan Sunnah Hari Ini */}
                    <div className="pt-2">
                        <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-2xl bg-emerald-50 border border-emerald-200 text-xs font-bold text-emerald-950 shadow-2xs">
                            <span>Progres Sunnahmu Hari Ini: <strong className="text-emerald font-black text-sm">{completedSunnahCount}</strong> dari {SUNNAH_LIST.length} Amalan Telah Dijalankan</span>
                        </div>
                    </div>
                </div>

                {/* Filter Kategori Sunnah */}
                <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
                    <button
                        onClick={() => setSunnahFilter('all')}
                        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${sunnahFilter === 'all'
                            ? 'bg-[#0F5B30] text-white shadow-xs'
                            : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        Semua Sunnah ({SUNNAH_LIST.length})
                    </button>
                    <button
                        onClick={() => setSunnahFilter('sahur_buka')}
                        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${sunnahFilter === 'sahur_buka'
                            ? 'bg-[#0F5B30] text-white shadow-xs'
                            : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        Sahur & Berbuka
                    </button>
                    <button
                        onClick={() => setSunnahFilter('ibadah')}
                        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${sunnahFilter === 'ibadah'
                            ? 'bg-[#0F5B30] text-white shadow-xs'
                            : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        Ibadah & Al-Qur'an
                    </button>
                    <button
                        onClick={() => setSunnahFilter('akhlak')}
                        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${sunnahFilter === 'akhlak'
                            ? 'bg-[#0F5B30] text-white shadow-xs'
                            : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        Hati & Akhlak
                    </button>
                    <button
                        onClick={() => setSunnahFilter('sedekah')}
                        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${sunnahFilter === 'sedekah'
                            ? 'bg-[#0F5B30] text-white shadow-xs'
                            : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        Sedekah & Sosial
                    </button>
                    <button
                        onClick={() => setSunnahFilter('sepuluh_terakhir')}
                        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${sunnahFilter === 'sepuluh_terakhir'
                            ? 'bg-[#0F5B30] text-white shadow-xs'
                            : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        10 Malam Terakhir
                    </button>
                </div>

                {/* Cards Grid Sunnah */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredSunnahs.map((sunnah) => {
                        const isDone = !!practicedSunnah[sunnah.id];
                        return (
                            <div
                                key={sunnah.id}
                                className={`rounded-3xl p-6 shadow-sm border transition-all flex flex-col justify-between group ${isDone
                                    ? 'bg-emerald-50/40 border-emerald-300 ring-1 ring-emerald-200'
                                    : 'bg-white border-gray-100 hover:shadow-md hover:border-emerald-200'
                                    }`}
                            >
                                <div className="space-y-4">
                                    {/* Top Row: Category Badge & Status */}
                                    <div className="flex items-center justify-between">
                                        <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-gold/15 text-amber-900 border border-gold/30">
                                            {sunnah.categoryLabel}
                                        </span>
                                        {isDone && (
                                            <span className="flex items-center gap-1 text-[11px] font-bold text-emerald bg-emerald-100/70 px-2.5 py-0.5 rounded-full">
                                                <CheckCircle2 size={13} />
                                                <span>Sudah Diamalkan</span>
                                            </span>
                                        )}
                                    </div>

                                    {/* Judul Sunnah */}
                                    <h3 className="text-lg font-black font-outfit text-gray-900 group-hover:text-emerald transition-colors leading-snug">
                                        {sunnah.title}
                                    </h3>

                                    {/* Teks Doa Khusus (Jika Ada) */}
                                    {sunnah.arabic && (
                                        <div className="p-4 rounded-2xl bg-emerald-950 text-white space-y-2.5 border border-emerald-800 shadow-inner">
                                            <div className="flex items-center justify-between text-[10px] text-gold font-bold uppercase tracking-wider">
                                                <span>Lafadz Doa Shahih</span>
                                                <button
                                                    onClick={() => handleCopyDoa(sunnah.arabic + '\n\n' + sunnah.latin + '\n\nArtinya: ' + sunnah.translation, sunnah.title)}
                                                    className="p-1 px-2 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center gap-1 transition-all cursor-pointer text-[10px]"
                                                    title="Salin Doa"
                                                >
                                                    <Copy size={11} />
                                                    <span>Salin Doa</span>
                                                </button>
                                            </div>
                                            <p className="text-right font-serif text-lg sm:text-xl leading-relaxed text-amber-100" dir="rtl">
                                                {sunnah.arabic}
                                            </p>
                                            <p className="text-xs text-emerald-200 font-medium italic">
                                                "{sunnah.latin}"
                                            </p>
                                            <p className="text-[11px] text-emerald-100/80 pt-1 border-t border-white/10">
                                                <strong>Artinya:</strong> {sunnah.translation}
                                            </p>
                                        </div>
                                    )}

                                    {/* Dalil & Rujukan Hadits */}
                                    <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100 space-y-1">
                                        <p className="text-xs text-gray-700 font-medium italic leading-relaxed">
                                            "{sunnah.dalil}"
                                        </p>
                                        <span className="block text-[10px] font-bold text-emerald-800 uppercase tracking-wider text-right">
                                            — {sunnah.source}
                                        </span>
                                    </div>

                                    {/* Panduan Praktis & Keutamaan */}
                                    <div className="space-y-2 text-xs text-gray-600 font-medium leading-relaxed">
                                        <div className="flex items-start gap-2">
                                            <span className="text-emerald font-bold shrink-0">✨ Tips:</span>
                                            <span>{sunnah.tips}</span>
                                        </div>
                                        <div className="flex items-start gap-2 text-emerald-900/90 bg-emerald-50/60 p-2.5 rounded-xl border border-emerald-100">
                                            <span className="font-bold text-gold shrink-0">🎁 Fadhilah:</span>
                                            <span>{sunnah.fadhilah}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Tombol Amalkan Hari Ini */}
                                <button
                                    onClick={() => toggleSunnahPractice(sunnah.id)}
                                    className={`mt-5 w-full py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer border ${isDone
                                        ? 'bg-emerald text-white border-emerald shadow-xs'
                                        : 'bg-white border-gray-200 text-gray-700 hover:border-emerald hover:text-emerald hover:bg-emerald-50/30'
                                        }`}
                                >
                                    {isDone ? (
                                        <>
                                            <CheckCircle2 size={15} />
                                            <span>Alhamdulillah, Sudah Diamalkan</span>
                                        </>
                                    ) : (
                                        <>
                                            <Circle size={15} className="text-gray-400" />
                                            <span>Tandai Diamalkan Hari Ini</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </AnimatedSection>

            {/* 4. JADWAL IMSAKIYAH LENGKAP */}
            <AnimatedSection id="jadwal-imsakiyah" className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <div className="bg-gradient-to-br from-[#0F5B30] to-[#0A3D20] text-white rounded-3xl p-8 lg:p-12 shadow-xl border border-emerald-600/30">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 pb-6 border-b border-white/10">
                        <div className="space-y-2">
                            <span className="text-gold text-xs font-bold uppercase tracking-wider block">
                                Waktu Sholat & Imsakiyah Harian
                            </span>
                            <h2 className="text-2xl sm:text-3xl font-black font-outfit text-white">
                                Jadwal Sholat Wilayah {schedule.name}
                            </h2>
                            <p className="text-xs sm:text-sm text-emerald-100 font-medium">
                                Acuan waktu adzan dan imsak berdasarkan hisab Kementerian Agama RI.
                            </p>
                        </div>

                        <div className="text-right">
                            <span className="text-xs text-emerald-200 font-medium block">Waktu Saat Ini:</span>
                            <span className="text-2xl font-black font-outfit text-gold font-mono">
                                {currentTime.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })} WIB
                            </span>
                        </div>
                    </div>

                    {/* Cards Grid 7 Waktu Sholat */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 text-center">
                        <div className="p-4 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-sm">
                            <span className="text-[11px] font-bold text-emerald-200 uppercase tracking-wider block mb-1">Imsak</span>
                            <span className="text-2xl font-black font-outfit text-white">{schedule.imsak}</span>
                            <span className="text-[10px] text-emerald-300 block mt-1">WIB</span>
                        </div>
                        <div className="p-4 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-sm">
                            <span className="text-[11px] font-bold text-emerald-200 uppercase tracking-wider block mb-1">Subuh</span>
                            <span className="text-2xl font-black font-outfit text-white">{schedule.subuh}</span>
                            <span className="text-[10px] text-emerald-300 block mt-1">WIB</span>
                        </div>
                        <div className="p-4 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-sm">
                            <span className="text-[11px] font-bold text-emerald-200 uppercase tracking-wider block mb-1">Terbit</span>
                            <span className="text-2xl font-black font-outfit text-white">{schedule.terbit}</span>
                            <span className="text-[10px] text-emerald-300 block mt-1">WIB</span>
                        </div>
                        <div className="p-4 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-sm">
                            <span className="text-[11px] font-bold text-emerald-200 uppercase tracking-wider block mb-1">Dzuhur</span>
                            <span className="text-2xl font-black font-outfit text-white">{schedule.dzuhur}</span>
                            <span className="text-[10px] text-emerald-300 block mt-1">WIB</span>
                        </div>
                        <div className="p-4 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-sm">
                            <span className="text-[11px] font-bold text-emerald-200 uppercase tracking-wider block mb-1">Ashar</span>
                            <span className="text-2xl font-black font-outfit text-white">{schedule.ashar}</span>
                            <span className="text-[10px] text-emerald-300 block mt-1">WIB</span>
                        </div>
                        <div className="p-4 rounded-2xl bg-amber-400/20 border-2 border-gold/70 backdrop-blur-sm shadow-md">
                            <span className="text-[11px] font-bold text-gold uppercase tracking-wider block mb-1">Maghrib (Buka)</span>
                            <span className="text-2xl font-black font-outfit text-gold">{schedule.maghrib}</span>
                            <span className="text-[10px] text-amber-200 block mt-1 font-bold">Waktu Berbuka</span>
                        </div>
                        <div className="p-4 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-sm">
                            <span className="text-[11px] font-bold text-emerald-200 uppercase tracking-wider block mb-1">Isya</span>
                            <span className="text-2xl font-black font-outfit text-white">{schedule.isya}</span>
                            <span className="text-[10px] text-emerald-300 block mt-1">WIB</span>
                        </div>
                    </div>
                </div>
            </AnimatedSection>

            {/* 4. NUTRISI & MENU SEHAT MUSLIMAH */}
            <AnimatedSection id="nutrisi-menu" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <div className="text-center max-w-3xl mx-auto mb-14 space-y-3">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 font-bold text-xs tracking-wider uppercase">
                        <Apple size={14} />
                        <span>Nutrisi & Gizi Sehat Ramadhan</span>
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-black font-outfit text-gray-900 tracking-tight">
                        Panduan Sahur & Berbuka Anti-Lemas / Begah
                    </h2>
                    <p className="text-sm sm:text-base text-gray-600 font-medium leading-relaxed">
                        Kurasi hidangan ramah hormon muslimah, menjaga gula darah stabil, dan aturan hidrasi 2-4-2 agar ibadah tilawah dan tarawih tetap bertenaga maksimal.
                    </p>
                </div>

                {/* WATER TRACKER ATURAN 2-4-2 */}
                <div className="mb-14 bg-gradient-to-r from-blue-50 via-sky-50 to-indigo-50 rounded-3xl p-6 sm:p-8 border border-blue-200/60 shadow-xs">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-6">
                        <div>
                            <span className="text-[11px] font-bold text-blue-700 uppercase tracking-wider block mb-1">
                                Water Intake Tracker (Aturan 2-4-2)
                            </span>
                            <h3 className="text-xl font-black font-outfit text-gray-900">
                                Target 8 Gelas Air Putih Sehari
                            </h3>
                            <p className="text-xs text-gray-600 font-medium mt-1">
                                <strong>2 Gelas</strong> saat Berbuka • <strong>4 Gelas</strong> sepanjang Malam • <strong>2 Gelas</strong> saat Sahur.
                            </p>
                        </div>

                        <div className="flex items-center gap-3">
                            <span className="text-2xl font-black font-outfit text-blue-600">
                                {waterGlasses} / 8 Gelas
                            </span>
                            <span className="text-xs font-bold px-3 py-1 bg-blue-600 text-white rounded-full">
                                {waterGlasses >= 8 ? '💧 Hidrasi Tercapai!' : `${8 - waterGlasses} Gelas Lagi`}
                            </span>
                        </div>
                    </div>

                    {/* Interactive Glasses */}
                    <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
                        {Array.from({ length: 8 }).map((_, idx) => {
                            const isFilled = idx < waterGlasses;
                            let phaseLabel = 'Malam';
                            if (idx < 2) phaseLabel = 'Berbuka';
                            else if (idx >= 6) phaseLabel = 'Sahur';

                            return (
                                <button
                                    key={idx}
                                    onClick={() => handleGlassClick(idx)}
                                    className={`p-3.5 rounded-2xl flex flex-col items-center justify-center transition-all cursor-pointer border ${isFilled
                                        ? 'bg-blue-600 text-white border-blue-600 shadow-sm shadow-blue-500/30 scale-102'
                                        : 'bg-white text-gray-400 border-blue-200 hover:border-blue-400'
                                        }`}
                                    title={`Gelas ${idx + 1} (${phaseLabel})`}
                                >
                                    <Droplets size={22} className={isFilled ? 'text-cyan-200 fill-cyan-200' : 'text-gray-300'} />
                                    <span className="text-[11px] font-black mt-1">Gelas {idx + 1}</span>
                                    <span className="text-[9px] uppercase tracking-wider opacity-80">{phaseLabel}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Search Bar & Filter Controls */}
                <div className="max-w-2xl mx-auto mb-6">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            value={menuSearch}
                            onChange={(e) => setMenuSearch(e.target.value)}
                            placeholder="Cari menu, bahan, atau hidangan (contoh: kurma, oatmeal, ayam panggang, timun suri)..."
                            className="w-full pl-11 pr-10 py-3 rounded-2xl bg-white border border-gray-200 text-sm font-medium text-gray-900 placeholder:text-gray-400 focus:outline-hidden focus:ring-2 focus:ring-emerald/20 focus:border-emerald shadow-xs transition-all"
                        />
                        {menuSearch && (
                            <button
                                onClick={() => setMenuSearch('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center text-xs font-bold transition-colors cursor-pointer"
                                title="Hapus pencarian"
                            >
                                <X size={13} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Filter Menu Tabs */}
                <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
                    <button
                        onClick={() => setMenuFilter('all')}
                        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${menuFilter === 'all' ? 'bg-[#0F5B30] text-white shadow-xs' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    >
                        Semua Menu ({HEALTHY_MENUS.length})
                    </button>
                    <button
                        onClick={() => setMenuFilter('sahur')}
                        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${menuFilter === 'sahur' ? 'bg-[#0F5B30] text-white shadow-xs' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    >
                        Sahur Padat Nutrisi ({HEALTHY_MENUS.filter(m => m.category === 'sahur').length})
                    </button>
                    <button
                        onClick={() => setMenuFilter('takjil')}
                        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${menuFilter === 'takjil' ? 'bg-[#0F5B30] text-white shadow-xs' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    >
                        Takjil Rendah Gula ({HEALTHY_MENUS.filter(m => m.category === 'takjil').length})
                    </button>
                    <button
                        onClick={() => setMenuFilter('iftar')}
                        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${menuFilter === 'iftar' ? 'bg-[#0F5B30] text-white shadow-xs' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    >
                        Buka Puasa Seimbang ({HEALTHY_MENUS.filter(m => m.category === 'iftar').length})
                    </button>
                </div>

                {/* Result count when searching */}
                {menuSearch.trim() && (
                    <div className="text-center text-xs text-gray-500 font-medium mb-8">
                        Ditemukan <strong className="text-emerald font-bold">{filteredMenus.length}</strong> resep untuk kata kunci "{menuSearch}"
                    </div>
                )}

                {/* Empty State */}
                {filteredMenus.length === 0 ? (
                    <div className="bg-white rounded-3xl p-10 text-center border border-gray-100 max-w-md mx-auto shadow-xs my-8">
                        <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center">
                            <Search size={26} />
                        </div>
                        <h4 className="text-base font-black text-gray-900 mb-1">Menu Tidak Ditemukan</h4>
                        <p className="text-xs text-gray-500 mb-5 leading-relaxed">
                            Tidak ada hidangan yang cocok dengan kriteria filter atau pencarian Anda.
                        </p>
                        <button
                            onClick={() => { setMenuSearch(''); setMenuFilter('all'); }}
                            className="px-4 py-2 rounded-xl bg-emerald text-white text-xs font-bold hover:bg-emerald-dark transition-all cursor-pointer"
                        >
                            Reset Filter & Pencarian
                        </button>
                    </div>
                ) : (
                    /* Cards Grid Menu */
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredMenus.map((menu) => (
                        <div
                            key={menu.id}
                            className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between hover:shadow-md transition-all group"
                        >
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald border border-emerald-100">
                                        {menu.tag}
                                    </span>
                                    <span className="text-xs font-black text-amber-700 bg-amber-50 px-2.5 py-1 rounded-lg">
                                        ~{menu.calories} kkal
                                    </span>
                                </div>

                                <h4 className="text-lg font-black font-outfit text-gray-900 group-hover:text-emerald transition-colors leading-snug">
                                    {menu.title}
                                </h4>

                                <p className="text-xs text-gray-600 font-medium leading-relaxed">
                                    {menu.description}
                                </p>

                                {/* Makronutrien Pill Grid */}
                                <div className="grid grid-cols-3 gap-2 text-center p-3 rounded-2xl bg-gray-50 border border-gray-100 text-[11px] font-bold">
                                    <div>
                                        <span className="text-[10px] text-gray-400 block font-normal">Protein</span>
                                        <span className="text-gray-800 font-black">{menu.protein}g</span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] text-gray-400 block font-normal">Karbohidrat</span>
                                        <span className="text-gray-800 font-black">{menu.carbs}g</span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] text-gray-400 block font-normal">Serat</span>
                                        <span className="text-emerald font-black">{menu.fiber}g</span>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => setSelectedMenuDetail(menu)}
                                className="mt-5 w-full py-2.5 rounded-xl border border-emerald/30 text-emerald font-bold text-xs hover:bg-emerald hover:text-white transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                            >
                                <span>Lihat Rincian & Tips</span>
                                <ChevronRight size={14} />
                            </button>
                        </div>
                    ))}
                    </div>
                )}
            </AnimatedSection>

            {/* MODAL DETAIL MENU */}
            {selectedMenuDetail && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 space-y-5 border border-gray-100 shadow-2xl relative">
                        <div className="flex items-start justify-between">
                            <div>
                                <span className="text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald">
                                    {selectedMenuDetail.tag}
                                </span>
                                <h3 className="text-xl font-black font-outfit text-gray-900 mt-2">
                                    {selectedMenuDetail.title}
                                </h3>
                            </div>
                            <button
                                onClick={() => setSelectedMenuDetail(null)}
                                className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 flex items-center justify-center text-sm font-bold cursor-pointer"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Bahan-bahan */}
                        <div>
                            <span className="text-xs font-black uppercase text-gray-400 block mb-2">Bahan & Komposisi:</span>
                            <ul className="space-y-1.5 text-xs text-gray-700 font-medium">
                                {selectedMenuDetail.ingredients.map((ing, i) => (
                                    <li key={i} className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald shrink-0" />
                                        <span>{ing}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Tips */}
                        <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-950 font-medium">
                            <strong className="font-bold text-emerald-900 block mb-1">💡 Tips Penyajian:</strong>
                            {selectedMenuDetail.tips}
                        </div>

                        <button
                            onClick={() => setSelectedMenuDetail(null)}
                            className="w-full py-3 rounded-xl bg-emerald text-white font-bold text-xs hover:bg-emerald-dark transition-all cursor-pointer"
                        >
                            Tutup
                        </button>
                    </div>
                </div>
            )}

            {/* 5. MUTABA'AH YAUMIYAH (DAILY TRACKER) */}
            <AnimatedSection id="mutabaah" className="py-20 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
                <div className="bg-white rounded-3xl p-6 sm:p-10 shadow-sm border border-gray-100 space-y-8">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-gray-100">
                        <div>
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald text-xs font-bold uppercase mb-2">
                                <CheckCircle2 size={14} />
                                <span>Mutaba'ah Yaumiyah</span>
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-black font-outfit text-gray-900">
                                Checklist Amalan Harian Muslimah
                            </h2>
                            <p className="text-xs text-gray-500 font-medium">
                                Catat amalan harian Anda. Progres hari ini tersimpan otomatis di browser Anda.
                            </p>
                        </div>

                        <div className="text-right">
                            <span className="text-[11px] font-bold text-gray-400 uppercase block">Capaian Hari Ini</span>
                            <span className="text-3xl font-black font-outfit text-emerald">
                                {mutabaahPercentage}%
                            </span>
                            <span className="text-[11px] text-gray-500 block font-semibold">
                                {completedDeedsCount} dari {DAILY_DEEDS.length} Amalan
                            </span>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden p-0.5 border border-gray-200">
                        <div
                            className="bg-gradient-to-r from-emerald to-gold h-full rounded-full transition-all duration-500 shadow-sm"
                            style={{ width: `${mutabaahPercentage}%` }}
                        />
                    </div>

                    {/* Checklist Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {DAILY_DEEDS.map((deed) => {
                            const isDone = !!mutabaahState[deed.id];
                            return (
                                <button
                                    key={deed.id}
                                    onClick={() => toggleMutabaah(deed.id)}
                                    className={`p-4 rounded-2xl flex items-start gap-3.5 text-left transition-all cursor-pointer border ${isDone
                                        ? 'bg-emerald-50/70 border-emerald-300 shadow-2xs'
                                        : 'bg-gray-50/60 border-gray-200 hover:bg-gray-100/70'
                                        }`}
                                >
                                    <div className="pt-0.5 shrink-0">
                                        {isDone ? (
                                            <CheckCircle2 size={20} className="text-emerald fill-emerald-100" />
                                        ) : (
                                            <Circle size={20} className="text-gray-300" />
                                        )}
                                    </div>
                                    <div>
                                        <h4 className={`text-sm font-bold ${isDone ? 'text-emerald-950 font-black line-through opacity-85' : 'text-gray-800'}`}>
                                            {deed.label}
                                        </h4>
                                        <p className="text-[11px] text-gray-500 font-medium mt-0.5">
                                            {deed.detail}
                                        </p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </AnimatedSection>

            {/* 6. KHAZANAH BERBAGI (RAMADHAN GIVING) */}
            <AnimatedSection className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <div className="bg-gradient-to-br from-[#0B4A28] via-[#0F5B30] to-[#126838] text-white rounded-3xl p-8 lg:p-14 shadow-2xl relative overflow-hidden">
                    <div className="max-w-2xl space-y-4 relative z-10">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-gold text-xs font-bold uppercase">
                            <Heart size={14} />
                            <span>Khazanah Berbagi (Ramadhan Giving)</span>
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-black font-outfit text-white leading-tight">
                            Sempurnakan Ramadhan dengan Sedekah & Infaq Terbaik
                        </h2>
                        <p className="text-sm sm:text-base text-emerald-100 font-medium leading-relaxed">
                            Salurkan ifthar jamaah, santunan santriwati dhuafa, dan program dakwah binaan muslimah Khazanah Alwahda Kreatif.
                        </p>

                        <div className="flex flex-wrap gap-3 pt-3">
                            <Link
                                to="/program"
                                className="px-6 py-3 rounded-xl bg-gold hover:bg-gold/90 text-gray-900 font-black text-xs sm:text-sm shadow-md transition-all flex items-center gap-2"
                            >
                                <span>Lihat Program Dakwah</span>
                                <ArrowRight size={16} />
                            </Link>
                            <Link
                                to="/kontak"
                                className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold text-xs sm:text-sm transition-all"
                            >
                                Hubungi Panitia Ramadhan
                            </Link>
                        </div>
                    </div>
                </div>
            </AnimatedSection>
        </div>
    );
};

export default RamadhanHub;
